import {
  COST_KIND_LABELS,
  WORK_ORDER_SOURCE_LABELS,
  WORK_ORDER_STATUS_LABELS,
  WORK_ORDER_TYPE_LABELS,
  type WorkOrderAction,
  type WorkOrderDetail,
} from "@station/contracts";
import { Alert, Button, Card, EmptyState, MoneyText, SkeletonRows, useToast } from "@station/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Play, SearchX, Trash2 } from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatDecimal } from "../../../../features/maintenance/maintenance-values.js";
import {
  useMaintenancePermissions,
  useRefreshMaintenance,
  useWorkOrder,
} from "../../../../features/maintenance/queries.js";
import {
  DetailRow,
  MaintenancePage,
  PriorityBadge,
  SlaBadge,
  WorkOrderStatusBadge,
} from "../../../../features/maintenance/shared.js";
import {
  AssignDialog,
  CostDialog,
  ExpenseDialog,
  type NoteAction,
  NoteActionDialog,
  PhotoUploadButton,
} from "../../../../features/maintenance/work-order-dialogs.js";
import { api } from "../../../../lib/api.js";
import { errorMessage, isApiError } from "../../../../lib/errors.js";
import { formatDate, formatDateTime } from "../../../../lib/format.js";
import { queryKeys } from "../../../../lib/query.js";

export const Route = createFileRoute("/_app/bakim/is-emirleri/$id")({ component: WorkOrderRoute });

/**
 * İş emri ayrıntısı (Prompt 23 web, `GET /maintenance/work-orders/:id`): özet, SLA, varlık / plan / alarm bağlantıları,
 * maliyet satırları ve toplamı, fotoğraflar, zaman çizelgesi ve sunucunun izin verdiği eylemler (`allowedActions`).
 */
function WorkOrderRoute(): ReactElement {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const permissions = useMaintenancePermissions();
  const order = useWorkOrder(id, permissions.enabled && permissions.canRead);
  const title = order.data?.title ?? t("maintenance.workOrders.detailTitle");
  return (
    <MaintenancePage
      title={title}
      {...(order.data
        ? { description: `${order.data.workOrderNo} · ${order.data.stationCode} ${order.data.stationName}` }
        : {})}
      breadcrumb={[
        {
          label: t("menu.maintenanceWorkOrders"),
          link: (children, className) => (
            <Link to="/bakim/is-emirleri" className={className}>
              {children}
            </Link>
          ),
        },
        { label: order.data?.workOrderNo ?? title },
      ]}
      actions={order.data ? <Actions order={order.data} /> : null}
    >
      {order.error ? (
        isApiError(order.error, "NOT_FOUND") ? (
          <EmptyState
            headingLevel={2}
            icon={<SearchX />}
            title={t("maintenance.workOrders.notFoundTitle")}
            description={t("maintenance.workOrders.notFoundDescription")}
          />
        ) : (
          <Alert tone="danger">{errorMessage(order.error)}</Alert>
        )
      ) : !order.data ? (
        <SkeletonRows rows={8} />
      ) : (
        <WorkOrderBody order={order.data} />
      )}
    </MaintenancePage>
  );
}

function Actions({ order }: { order: WorkOrderDetail }): ReactElement | null {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshMaintenance();
  const [dialog, setDialog] = useState<WorkOrderAction | null>(null);
  const allowed = new Set(order.allowedActions);
  const direct = useMutation({
    mutationFn: async (action: "START" | "RESUME") =>
      (
        await api.POST(
          action === "START" ? "/maintenance/work-orders/{id}/start" : "/maintenance/work-orders/{id}/resume",
          {
            params: { path: { id: order.id } },
            body: { version: order.version },
          },
        )
      ).data!,
    onSuccess: async (_data, action) => {
      await refresh();
      toast({ tone: "success", title: t(`maintenance.actionDone.${action}`) });
    },
    onError: async (caught) => {
      await refresh();
      toast({ tone: "danger", title: errorMessage(caught) });
    },
  });
  if (allowed.size === 0) return null;
  const noteActions: NoteAction[] = ["HOLD", "COMPLETE", "CANCEL", "NOTE"];
  return (
    <div className="flex flex-wrap gap-2">
      {allowed.has("START") ? (
        <Button
          loading={direct.isPending && direct.variables === "START"}
          onClick={() => direct.mutate("START")}
        >
          <Play aria-hidden="true" />
          {t("maintenance.actions.START")}
        </Button>
      ) : null}
      {allowed.has("RESUME") ? (
        <Button
          loading={direct.isPending && direct.variables === "RESUME"}
          onClick={() => direct.mutate("RESUME")}
        >
          <Play aria-hidden="true" />
          {t("maintenance.actions.RESUME")}
        </Button>
      ) : null}
      {allowed.has("COMPLETE") ? (
        <Button onClick={() => setDialog("COMPLETE")}>{t("maintenance.actions.COMPLETE")}</Button>
      ) : null}
      {allowed.has("ASSIGN") ? (
        <Button variant="outline" onClick={() => setDialog("ASSIGN")}>
          {t("maintenance.actions.ASSIGN")}
        </Button>
      ) : null}
      {allowed.has("HOLD") ? (
        <Button variant="outline" onClick={() => setDialog("HOLD")}>
          {t("maintenance.actions.HOLD")}
        </Button>
      ) : null}
      {allowed.has("ADD_COST") ? (
        <Button variant="outline" onClick={() => setDialog("ADD_COST")}>
          {t("maintenance.actions.ADD_COST")}
        </Button>
      ) : null}
      {allowed.has("ADD_PHOTO") ? <PhotoUploadButton order={order} /> : null}
      {allowed.has("CREATE_EXPENSE") ? (
        <Button variant="outline" onClick={() => setDialog("CREATE_EXPENSE")}>
          {t("maintenance.actions.CREATE_EXPENSE")}
        </Button>
      ) : null}
      {allowed.has("NOTE") ? (
        <Button variant="ghost" onClick={() => setDialog("NOTE")}>
          {t("maintenance.actions.NOTE")}
        </Button>
      ) : null}
      {allowed.has("CANCEL") ? (
        <Button variant="ghost" onClick={() => setDialog("CANCEL")}>
          {t("maintenance.actions.CANCEL")}
        </Button>
      ) : null}
      {dialog === "ASSIGN" ? <AssignDialog open onOpenChange={() => setDialog(null)} order={order} /> : null}
      {dialog === "ADD_COST" ? <CostDialog open onOpenChange={() => setDialog(null)} order={order} /> : null}
      {dialog === "CREATE_EXPENSE" ? (
        <ExpenseDialog open onOpenChange={() => setDialog(null)} order={order} />
      ) : null}
      {dialog && (noteActions as string[]).includes(dialog) ? (
        <NoteActionDialog
          open
          onOpenChange={() => setDialog(null)}
          order={order}
          action={dialog as NoteAction}
        />
      ) : null}
    </div>
  );
}

function WorkOrderBody({ order }: { order: WorkOrderDetail }): ReactElement {
  const { t } = useTranslation();
  return (
    <>
      <Card className="grid gap-4 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <WorkOrderStatusBadge status={order.status} />
          <PriorityBadge priority={order.priority} />
          <SlaBadge sla={order.sla} />
        </div>
        <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <DetailRow label={t("maintenance.fields.workType")}>
            {WORK_ORDER_TYPE_LABELS[order.workType]}
          </DetailRow>
          <DetailRow label={t("maintenance.fields.source")}>
            {WORK_ORDER_SOURCE_LABELS[order.source]}
          </DetailRow>
          <DetailRow label={t("maintenance.fields.asset")}>
            {order.asset ? (
              <Link
                to="/bakim/varliklar/$kind/$id"
                params={{ kind: order.asset.kind, id: order.asset.id }}
                className="underline underline-offset-4"
              >
                {order.asset.name}
              </Link>
            ) : (
              t("maintenance.stationWide")
            )}
          </DetailRow>
          <DetailRow label={t("maintenance.fields.createdAt")}>{formatDateTime(order.createdAt)}</DetailRow>
          <DetailRow label={t("maintenance.fields.responseDueAt")}>
            {formatDateTime(order.responseDueAt)}
          </DetailRow>
          <DetailRow label={t("maintenance.fields.resolveDueAt")}>
            {formatDateTime(order.resolveDueAt)}
          </DetailRow>
          <DetailRow label={t("maintenance.fields.respondedAt")}>
            {formatDateTime(order.respondedAt)}
          </DetailRow>
          <DetailRow label={t("maintenance.fields.completedAt")}>
            {formatDateTime(order.completedAt)}
          </DetailRow>
          <DetailRow label={t("maintenance.fields.serviceCounterparty")}>
            {order.serviceCounterparty?.name ?? "—"}
          </DetailRow>
          <DetailRow label={t("maintenance.fields.assignedUser")}>
            {order.assignedUser?.name ?? "—"}
          </DetailRow>
          {order.plan ? (
            <DetailRow label={t("maintenance.fields.plan")}>
              {order.plan.name}
              {order.dueOn ? ` · ${t("maintenance.fields.dueOn")} ${formatDate(order.dueOn)}` : ""}
            </DetailRow>
          ) : null}
          {order.alarm ? (
            <DetailRow label={t("maintenance.fields.alarm")}>
              <Link
                to="/alarmlar/$alarmId"
                params={{ alarmId: order.alarm.id }}
                className="underline underline-offset-4"
              >
                {order.alarm.title}
              </Link>
            </DetailRow>
          ) : null}
          {order.completionMeter ? (
            <DetailRow label={t("maintenance.fields.completionMeter")}>
              {formatDecimal(order.completionMeter)}
            </DetailRow>
          ) : null}
          {order.expense ? (
            <DetailRow label={t("maintenance.fields.expense")}>
              <Link
                to="/giderler/$id"
                params={{ id: order.expense.id }}
                className="underline underline-offset-4"
              >
                {order.expense.expenseNo ?? t("maintenance.expense.draft")}
              </Link>
            </DetailRow>
          ) : null}
        </dl>
        {order.description ? <p className="whitespace-pre-line text-sm">{order.description}</p> : null}
        {order.resolutionNote ? (
          <Alert tone="success" title={t("maintenance.fields.resolutionNote")}>
            {order.resolutionNote}
          </Alert>
        ) : null}
        {order.cancelReason ? (
          <Alert tone="info" title={t("maintenance.fields.cancelReason")}>
            {order.cancelReason}
          </Alert>
        ) : null}
      </Card>
      <Costs order={order} />
      {order.photos.length > 0 ? <Photos order={order} /> : null}
      <Timeline order={order} />
    </>
  );
}

function Costs({ order }: { order: WorkOrderDetail }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshMaintenance();
  const canEdit = order.allowedActions.includes("ADD_COST");
  const remove = useMutation({
    mutationFn: async (costId: string) =>
      (
        await api.DELETE("/maintenance/work-orders/{id}/costs/{costId}", {
          params: { path: { id: order.id, costId } },
        })
      ).data!,
    onSuccess: async () => {
      await refresh();
      toast({ tone: "success", title: t("maintenance.costs.removed") });
    },
    onError: (caught) => toast({ tone: "danger", title: errorMessage(caught) }),
  });
  return (
    <Card className="grid gap-3 p-4">
      <h2 className="text-base font-semibold">{t("maintenance.costs.title")}</h2>
      {order.costs.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("maintenance.costs.none")}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <caption className="sr-only">{t("maintenance.costs.title")}</caption>
            <thead>
              <tr className="border-b text-left text-xs text-muted-foreground">
                <th scope="col" className="py-2 pr-3">
                  {t("maintenance.fields.costKind")}
                </th>
                <th scope="col" className="py-2 pr-3">
                  {t("maintenance.fields.description")}
                </th>
                <th scope="col" className="py-2 pr-3 text-right">
                  {t("maintenance.fields.quantity")}
                </th>
                <th scope="col" className="py-2 pr-3 text-right">
                  {t("maintenance.fields.unitPrice")}
                </th>
                <th scope="col" className="py-2 pr-3 text-right">
                  {t("maintenance.fields.amount")}
                </th>
                <th scope="col" className="py-2 pr-3">
                  {t("maintenance.fields.vatRate")}
                </th>
                {canEdit ? (
                  <th scope="col" className="py-2">
                    <span className="sr-only">{t("common.actions")}</span>
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {order.costs.map((cost) => (
                <tr key={cost.id} className="border-b last:border-0">
                  <td className="py-2 pr-3">{COST_KIND_LABELS[cost.kind]}</td>
                  <td className="py-2 pr-3">{cost.description}</td>
                  <td className="tabular py-2 pr-3 text-right">{formatDecimal(cost.quantity)}</td>
                  <td className="tabular py-2 pr-3 text-right">{formatDecimal(cost.unitPrice)} ₺</td>
                  <td className="py-2 pr-3 text-right">
                    <MoneyText value={cost.amount} />
                  </td>
                  <td className="py-2 pr-3">{cost.vatRateCode}</td>
                  {canEdit ? (
                    <td className="py-2 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        loading={remove.isPending && remove.variables === cost.id}
                        aria-label={t("maintenance.costs.remove", { description: cost.description })}
                        onClick={() => remove.mutate(cost.id)}
                      >
                        <Trash2 aria-hidden="true" />
                      </Button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <dl className="grid gap-3 sm:grid-cols-4">
        <DetailRow label={COST_KIND_LABELS.PART}>
          <MoneyText value={order.costTotals.parts} />
        </DetailRow>
        <DetailRow label={COST_KIND_LABELS.LABOR}>
          <MoneyText value={order.costTotals.labor} />
        </DetailRow>
        <DetailRow label={COST_KIND_LABELS.OTHER}>
          <MoneyText value={order.costTotals.other} />
        </DetailRow>
        <DetailRow label={t("maintenance.costs.net")}>
          <MoneyText value={order.costTotals.net} className="font-semibold" />
        </DetailRow>
      </dl>
    </Card>
  );
}

function Photo({ fileId, alt }: { fileId: string; alt: string }): ReactElement {
  const { t } = useTranslation();
  const link = useQuery({
    queryKey: queryKeys.maintenance("work-order", { photo: fileId }),
    staleTime: 240_000,
    queryFn: async () =>
      (await api.GET("/files/{id}/download-link", { params: { path: { id: fileId } } })).data!,
  });
  return link.data ? (
    <a href={link.data.url} target="_blank" rel="noreferrer" className="block">
      <img
        src={link.data.url}
        alt={alt}
        loading="lazy"
        className="aspect-square w-full rounded object-cover"
      />
    </a>
  ) : (
    <div className="grid aspect-square place-items-center rounded bg-muted text-xs text-muted-foreground">
      {t("common.loading")}
    </div>
  );
}

function Photos({ order }: { order: WorkOrderDetail }): ReactElement {
  const { t } = useTranslation();
  return (
    <Card className="grid gap-3 p-4">
      <h2 className="text-base font-semibold">{t("maintenance.photos.title")}</h2>
      <ul className="grid grid-cols-2 gap-3 sm:grid-cols-4" aria-label={t("maintenance.photos.title")}>
        {order.photos.map((photo, index) => (
          <li key={photo.id} className="grid gap-1.5 rounded-md border p-2">
            <Photo
              fileId={photo.fileId}
              alt={t("maintenance.photos.alt", { index: index + 1, no: order.workOrderNo })}
            />
            <span className="text-xs text-muted-foreground">
              {photo.caption ? `${photo.caption} · ` : ""}
              {formatDateTime(photo.createdAt)}
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function Timeline({ order }: { order: WorkOrderDetail }): ReactElement {
  const { t } = useTranslation();
  return (
    <Card className="grid gap-3 p-4">
      <h2 className="text-base font-semibold">{t("maintenance.timeline.title")}</h2>
      <ol className="grid gap-3">
        {[...order.events].reverse().map((event) => (
          <li key={event.id} className="grid gap-0.5 border-l-2 pl-3">
            <span className="text-sm font-medium">
              {t(`maintenance.timeline.kinds.${event.kind}`)}
              {event.toStatus && event.kind === "STATUS_CHANGED"
                ? ` → ${WORK_ORDER_STATUS_LABELS[event.toStatus]}`
                : ""}
            </span>
            {event.note ? <span className="whitespace-pre-line text-sm">{event.note}</span> : null}
            <span className="text-xs text-muted-foreground">
              {event.actorName ?? t("maintenance.timeline.system")} · {formatDateTime(event.occurredAt)}
            </span>
          </li>
        ))}
      </ol>
    </Card>
  );
}
